import { useEffect, useState } from 'react';

/** Milliseconds a decision waits after its page is shown, and after every return to it, before it may be pressed */
export const ARMED = 500;

/**
 * Whether a consequential decision may be pressed (an installed client's Continue, an application's
 * Allow). A page that moves this one under the pointer just before a press (closing a window above it,
 * switching tabs) would otherwise turn a press meant for something else into a consent, so the button
 * waits `ARMED` after the page is shown and again whenever it is shown or focused anew, and is off while
 * it is hidden or not focused.
 *
 * @param shown Whether the decision is on the page at all; nothing is armed before it is
 */
export function useArmed(shown: boolean): boolean {
	const [armed, setArmed] = useState(false);
	useEffect(() => {
		if (!shown) return;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const disarm = () => {
			clearTimeout(timer);
			setArmed(false);
		};
		const arm = () => {
			disarm();
			if (document.visibilityState === 'visible') timer = setTimeout(() => setArmed(true), ARMED);
		};
		arm();
		document.addEventListener('visibilitychange', arm);
		globalThis.addEventListener('focus', arm);
		globalThis.addEventListener('blur', disarm);
		return () => {
			clearTimeout(timer);
			document.removeEventListener('visibilitychange', arm);
			globalThis.removeEventListener('focus', arm);
			globalThis.removeEventListener('blur', disarm);
		};
	}, [shown]);
	return armed;
}
